/**
 * Wishlist (theme-owned)
 * ----------------------
 * A single, lightweight controller + a self-syncing `<wishlist-button>` custom
 * element built on top of the Wishlist app proxy (`window.wishlist.apiBase`,
 * e.g. `/apps/wishlist/v1`).
 *
 * Responsibilities:
 *  1. On load (logged-in customers) fetch the count + list ONCE in parallel and
 *     cache them on `window.theme.wishlist.state`. The list adds `?moreInfo=true`
 *     (full item data) only when a `wishlist-carousel-component` or
 *     `wishlist-page-component` is on the page; elsewhere only variant ids are
 *     fetched for the heart toggles, cutting the on-load payload.
 *  2. Reflect the count in the header badge.
 *  3. Each `<wishlist-button data-product-id data-variant-id data-sku>` renders
 *     its own added/empty state from the cache on connect and whenever its
 *     `data-variant-id` changes (PLP/PDP variant change, AJAX re-render, quick
 *     view, sticky add-to-cart) — no global DOM-scanning observer required.
 *     Toggles start with the theme `.hidden` class and are revealed only when
 *     logged-out (immediate) or logged-in after the initial API sync sets
 *     `state.ready` (failed fetch keeps them hidden).
 *  4. Click behaviour:
 *       - Logged-out  -> store pending item + redirect to login with return_to
 *       - Not in list -> add    (POST `/wishlist`)
 *       - In list     -> remove (DELETE `/{variantId}`)
 *     Each change updates the cache + header and dispatches `wishlist:updated`.
 *
 * Public API: `window.theme.wishlist.{ state, isInWishlist, sync, add, remove,
 *   WishlistAdded, WishlistRemoved, refresh }`.
 *
 * External surfaces (Live TV app extension, etc.) that call the wishlist API
 * directly should notify the theme so the header badge + toggles stay in sync:
 *
 *   document.dispatchEvent(new CustomEvent('wishlist:updated', {
 *     bubbles: true,
 *     detail: { action: 'added', productId, variantId, sku },
 *   }));
 *
 *   document.dispatchEvent(new CustomEvent('wishlist:updated', {
 *     bubbles: true,
 *     detail: { action: 'removed', productId, variantId },
 *   }));
 *
 * Or call `window.theme.wishlist.WishlistAdded(...)` / `WishlistRemoved(...)` after
 * a successful API response. Theme-originated updates include `source: 'theme'` so
 * the inbound listener does not double-apply cache changes.
 */

import { ThemeEvents } from '@theme/events';

(() => {
  const win = /** @type {any} */ (window);

  const config = win.wishlist || (win.wishlist = {});
  const API_BASE = String(config.apiBase || config.apiBaseUrl || '/apps/wishlist/v1').replace(/\/+$/, '');
  const customer = config.customer || {};

  win.theme = win.theme || {};
  /** @type {any} */
  const controller = win.theme.wishlist || (win.theme.wishlist = {});

  const TAG = 'wishlist-button';
  const BADGE_SELECTOR = '.wishlist-header__badge';
  const PENDING_KEY = 'wishlist_pending_add';

  const state = { ready: false, count: 0, items: /** @type {any[]} */ ([]), variantSet: new Set() };

  const isLoggedIn = () => Boolean(customer && customer.isLoggedIn);

  /* --------------------------------------------------------------------- */
  /* Helpers                                                               */
  /* --------------------------------------------------------------------- */

  /**
   * Normalize any id-like value (numeric id or GID) to a comparable numeric string.
   * @param {string|number|null|undefined} value
   */
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const str = String(value).trim();
    const match = str.match(/\d+/);
    return match ? match[0] : str;
  };

  /**
   * Extract the variant id from a list item regardless of the backend key shape.
   * The API nests it at `item.variant.id`.
   * @param {any} item
   */
  const extractVariantId = (item) => {
    if (item === null || item === undefined) return '';
    if (typeof item === 'string' || typeof item === 'number') return normalizeId(item);
    return normalizeId(
      (item.variant && item.variant.id) ?? item.variantId ?? item.variant_id ?? item.id
    );
  };

  /** Rebuild the fast variant lookup set from the cached items. */
  const rebuildVariantSet = () => {
    state.variantSet.clear();
    for (const item of state.items) {
      const variantId = extractVariantId(item);
      if (variantId) state.variantSet.add(variantId);
    }
  };

  /** @param {string|number|null|undefined} variantId */
  const isInWishlist = (variantId) => {
    const id = normalizeId(variantId);
    return id !== '' && state.variantSet.has(id);
  };

  /* --------------------------------------------------------------------- */
  /* API                                                                   */
  /* --------------------------------------------------------------------- */

  /**
   * @param {string} path
   * @param {RequestInit} [options]
   */
  const request = async (path, options) => {
    const response = await fetch(`${API_BASE}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'x-client-type': 'web',
        ...(options && options.headers),
      },
    });
    if (!response.ok) throw new Error(`Wishlist request failed (${response.status}): ${path}`);
    const data = await response.json().catch(() => null);
    // The proxy answers 200 with `{ success: false, error }` for handled
    // failures (auth, validation, duplicates). Treat those as errors so the
    // optimistic UI reverts instead of silently keeping the toggled state.
    if (data && data.success === false) {
      throw new Error((data && data.error) || `Wishlist operation failed: ${path}`);
    }
    return data;
  };

  /**
   * Only the wishlist carousel and the wishlist page render full item data
   * (image, price, title, stock) from the shared init cache. Every other page
   * shows just `<wishlist-button>` hearts, which need variant ids only. Skip the
   * heavier `moreInfo=true` payload there to cut the on-load transfer size.
   */
  const needsFullList = () =>
    document.querySelector('wishlist-carousel-component, wishlist-page-component') != null;

  /** Fetch count + list together and cache the result. */
  const fetchState = async () => {
    const listPath = needsFullList() ? '/list?moreInfo=true' : '/list';
    const [countResponse, listResponse] = await Promise.all([
      request('/count'),
      request(listPath),
    ]);
    state.count = Number(countResponse && countResponse.data && countResponse.data.count) || 0;
    state.items =
      listResponse && listResponse.data && Array.isArray(listResponse.data.items)
        ? listResponse.data.items
        : [];
    rebuildVariantSet();
    state.ready = true;
    return state;
  };

  /**
   * @param {{ productId: any, variantId: any, sku: any }} payload
   */
  const apiAdd = (payload) =>
    request('/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_id: normalizeId(payload.productId),
        variant_id: normalizeId(payload.variantId),
        sku: payload.sku || '',
        email: customer.email || '',
        dynamic_id: (win.DY && win.DY.dyid) || '',
        dysessionid: (win.DY && win.DY.session) || '',
      }),
    });

  /** @param {string|number} variantId */
  const apiDelete = (variantId) => request(`/${normalizeId(variantId)}`, { method: 'DELETE' });

  /**
   * Full wishlist size from a `/list` payload. Paginated responses use `count`
   * for the current page size and `total` for the full wishlist size.
   * @param {any} data
   * @returns {number|null}
   */
  const resolveListTotalCount = (data) => {
    if (!data) return null;
    const total = Number(data.total);
    if (Number.isFinite(total)) return total;
    const hasPagination = data.page != null || data.limit != null;
    if (!hasPagination) {
      const count = Number(data.count);
      if (Number.isFinite(count)) return count;
    }
    return null;
  };

  /**
   * Upsert items into the cache by variant id, keeping cached entries that are
   * not in `incoming` (they belong to other pages of a paginated list).
   * @param {any[]} incoming
   */
  const mergeItems = (incoming) => {
    const fresh = new Map(incoming.map((item) => [extractVariantId(item), item]));
    state.items = state.items.map((item) => {
      const id = extractVariantId(item);
      const match = fresh.get(id);
      if (!match) return item;
      fresh.delete(id);
      return match;
    });
    state.items.push(...fresh.values());
    rebuildVariantSet();
  };

  /** @param {{ page?: number, limit?: number, moreInfo?: boolean, updateCache?: boolean }} [options] */
  const fetchList = async (options = {}) => {
    const page = options.page ?? 1;
    const limit = options.limit ?? 20;
    const moreInfo = options.moreInfo !== false;
    const updateCache = options.updateCache !== false;
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      moreInfo: String(moreInfo),
    });
    const result = await request(`/list?${params.toString()}`);
    if (updateCache && moreInfo && result && result.data) {
      const resolvedTotal = resolveListTotalCount(result.data);
      if (Array.isArray(result.data.items)) {
        const incoming = result.data.items;
        // A single page only replaces the cache when it is the whole wishlist;
        // otherwise every variant on later pages would drop out of `variantSet`.
        const isFullList =
          page === 1 &&
          (resolvedTotal != null ? incoming.length >= resolvedTotal : incoming.length > 0 && incoming.length < limit);
        if (isFullList) {
          state.items = incoming;
          rebuildVariantSet();
        } else if (incoming.length > 0) {
          mergeItems(incoming);
        }
      }
      if (resolvedTotal != null) {
        state.count = resolvedTotal;
        updateHeader();
      }
    }
    return result;
  };

  const removeAll = async () => {
    const result = await request('/all', { method: 'DELETE' });
    state.count = 0;
    state.items = [];
    rebuildVariantSet();
    state.ready = true;
    updateHeader();
    sync(document);
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: { action: 'cleared', count: 0, source: 'theme' },
      })
    );
    return result;
  };

  /**
   * @param {number|string} price
   * @param {string} [currency]
   * @param {string} [country]
   */
  const formatPrice = (price, currency = 'USD', country = 'US') => {
    const value = parseFloat(String(price || 0));
    let locale = 'en-US';
    if (country === 'CA') locale = 'en-CA';
    else if (country === 'GB') locale = 'en-GB';
    else if (country === 'AU') locale = 'en-AU';
    else if (country === 'DE') locale = 'de-DE';
    else if (country === 'FR') locale = 'fr-FR';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const restorePendingAdd = async () => {
    if (!isLoggedIn()) return;
    let pending = null;
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (raw) pending = JSON.parse(raw);
    } catch (error) {
      localStorage.removeItem(PENDING_KEY);
      return;
    }
    if (!pending || !pending.variantId) return;
    try {
      await add({
        productId: pending.productId,
        variantId: pending.variantId,
        sku: pending.sku,
      });
    } catch (error) {
      console.error('[Wishlist] restore pending add failed', error);
    } finally {
      localStorage.removeItem(PENDING_KEY);
    }
  };

  /* --------------------------------------------------------------------- */
  /* UI                                                                    */
  /* --------------------------------------------------------------------- */

  /** Reflect the current count in every header badge. */
  const updateHeader = () => {
    const value = Number(state.count) || 0;
    document.querySelectorAll(BADGE_SELECTOR).forEach((badge) => {
      if (!(badge instanceof HTMLElement)) return;
      badge.textContent = String(value);
      badge.setAttribute('data-count', String(value));
      badge.style.display = value > 0 ? '' : 'none';
    });
    document.querySelectorAll('wishlist-header').forEach((el) => {
      el.classList.toggle('has-items', value > 0);
    });
  };

  /**
   * Logged-out toggles show immediately (click → login). Logged-in toggles stay
   * hidden until the initial count/list fetch sets `state.ready`.
   */
  const isButtonVisible = () => !isLoggedIn() || state.ready;

  /**
   * Apply the added / empty visual state to a single `<wishlist-button>`.
   * @param {Element} el
   */
  const renderElement = (el) => {
    if (!(el instanceof Element)) return;
    const inList = isInWishlist(el.getAttribute('data-variant-id'));

    // Empty/filled icon visibility is driven purely by this class in CSS.
    // (SVG elements are SVGElement, not HTMLElement, so toggling `.hidden`
    // on them does not reliably reflect to the attribute.)
    el.classList.toggle('is-in-wishlist', inList);

    const button = el.querySelector('button') || el;
    button.setAttribute('aria-pressed', String(inList));
    // In the cart the visible label is the accessible name (Label-in-Name), so
    // don't override it with an aria-label. Elsewhere the icon-only button needs
    // an explicit, state-aware name.
    const isCart = el.getAttribute('data-context') === 'cart';
    if (isCart) {
      // Cart / mini-cart "Already in wishlist" is display-only — no remove action.
      button.setAttribute('aria-disabled', String(inList));
    } else {
      button.removeAttribute('aria-disabled');
      button.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
    }

    if (el instanceof HTMLElement) {
      el.classList.toggle('hidden', !isButtonVisible());
    }
  };

  /**
   * Sync every `<wishlist-button>` inside a target (or the target itself).
   * @param {ParentNode|Element|Document} [target=document]
   */
  const sync = (target = document) => {
    const root = target || document;
    if (root instanceof Element && typeof root.matches === 'function' && root.matches(TAG)) {
      renderElement(root);
    }
    if (typeof (/** @type {any} */ (root).querySelectorAll) === 'function') {
      root.querySelectorAll(TAG).forEach(renderElement);
    }
  };

  /**
   * Re-apply state to every toggle on the page matching a variant id.
   * @param {string|number|null|undefined} variantId
   */
  const syncByVariant = (variantId) => {
    const id = normalizeId(variantId);
    if (!id) return;
    document.querySelectorAll(`${TAG}[data-variant-id]`).forEach((el) => {
      if (normalizeId(el.getAttribute('data-variant-id')) === id) renderElement(el);
    });
  };

  /**
   * @param {'added'|'removed'} action
   * @param {string} variantId
   * @param {string} productId
   * @param {string} [sku]
   */
  const dispatchUpdated = (action, variantId, productId, sku) => {
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: {
          action,
          variantId,
          productId,
          sku: sku || '',
          count: state.count,
          source: 'theme',
        },
      })
    );
  };

  /* --------------------------------------------------------------------- */
  /* Mutations                                                             */
  /* --------------------------------------------------------------------- */

  /**
   * Apply an add to the local cache + UI (no network). Returns false if it was
   * already present.
   * @param {string} id
   * @param {any} productId
   * @param {any} sku
   */
  const applyAdd = (id, productId, sku) => {
    if (state.variantSet.has(id)) return false;
    state.items.push({ productId: normalizeId(productId), variant: { id }, sku: sku || null });
    state.variantSet.add(id);
    state.count += 1;
    updateHeader();
    syncByVariant(id);
    return true;
  };

  /**
   * Apply a remove to the local cache + UI (no network). Returns false if it was
   * not present.
   * @param {string} id
   */
  const applyRemove = (id) => {
    if (!state.variantSet.has(id)) return false;
    state.items = state.items.filter((item) => extractVariantId(item) !== id);
    state.variantSet.delete(id);
    state.count = Math.max(0, state.count - 1);
    updateHeader();
    syncByVariant(id);
    return true;
  };

  /**
   * Optimistic add: flip UI immediately, call the API, revert on failure.
   * @param {{ productId: any, variantId: any, sku?: any }} payload
   */
  const add = async ({ productId, variantId, sku }) => {
    const id = normalizeId(variantId);
    if (!id || isInWishlist(id)) return;
    if (!applyAdd(id, productId, sku)) return;
    try {
      await apiAdd({ productId, variantId: id, sku });
      dispatchUpdated('added', id, normalizeId(productId), sku);
    } catch (error) {
      applyRemove(id);
      throw error;
    }
  };

  /**
   * Sync local cache + UI after an external surface already called the wishlist
   * API (Live TV app extension, etc.). Does not perform a network request.
   * @param {{ productId: any, variantId: any, sku?: any }} payload
   * @param {{ notify?: boolean }} [options]
   */
  const syncExternalAdd = ({ productId, variantId, sku }, options = {}) => {
    const { notify = true } = options;
    const id = normalizeId(variantId);
    if (!id) return;
    if (applyAdd(id, productId, sku) && notify) {
      dispatchUpdated('added', id, normalizeId(productId), sku);
    }
  };

  /**
   * Sync local cache + UI after an external surface already removed via API.
   * @param {{ productId: any, variantId: any }} payload
   * @param {{ notify?: boolean }} [options]
   */
  const syncExternalRemove = ({ productId, variantId }, options = {}) => {
    const { notify = true } = options;
    const id = normalizeId(variantId);
    if (!id) return;
    const existing = state.items.filter((item) => extractVariantId(item) === id)[0];
    const sku = existing ? existing.sku : null;
    if (applyRemove(id) && notify) {
      dispatchUpdated('removed', id, normalizeId(productId), sku);
    }
  };

  /**
   * Public helper for external surfaces that prefer calling the theme API
   * instead of dispatching a DOM event.
   * @param {{ productId: any, variantId: any, sku?: any, source?: string }} payload
   */
  const WishlistAdded = (payload) => syncExternalAdd(payload);

  /**
   * @param {{ productId: any, variantId: any, source?: string }} payload
   */
  const WishlistRemoved = (payload) => syncExternalRemove(payload);

  /**
   * Optimistic remove: flip UI immediately, call the API, revert on failure.
   * @param {{ productId: any, variantId: any }} payload
   */
  const remove = async ({ productId, variantId }) => {
    const id = normalizeId(variantId);
    if (!id) return;
    const existing = state.items.filter((item) => extractVariantId(item) === id)[0];
    const sku = existing ? existing.sku : null;
    // The cache can miss items the server still has (e.g. rows rendered from a
    // later wishlist page), so a cache miss must still reach the API.
    const wasCached = applyRemove(id);
    try {
      await apiDelete(id);
      if (!wasCached) {
        state.count = Math.max(0, state.count - 1);
        updateHeader();
      }
      dispatchUpdated('removed', id, normalizeId(productId), sku);
    } catch (error) {
      if (wasCached) applyAdd(id, productId, sku);
      throw error;
    }
  };

  /* --------------------------------------------------------------------- */
  /* Logged-out handling                                                   */
  /* --------------------------------------------------------------------- */

  /** @param {{ productId: any, variantId: any, sku: any }} item */
  const storePending = (item) => {
    try {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          productId: item.productId || null,
          variantId: item.variantId || null,
          sku: item.sku || null,
          returnUrl: window.location.href,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      /* storage unavailable — ignore */
    }
  };

  const redirectToLogin = () => {
    const loginBase = String(config.loginUrl || '/account/login').split('?')[0];
    const returnTo = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.href = `${loginBase}?return_to=${returnTo}`;
  };

  /* --------------------------------------------------------------------- */
  /* Cart "move to wishlist"                                               */
  /* --------------------------------------------------------------------- */

  /**
   * Drop the cart line that owns this toggle by triggering the row's existing
   * remove control. This reuses the theme's cart flow end-to-end (AJAX update,
   * free-gift batching, row animation, section morph) instead of duplicating it.
   * @param {Element} el - The clicked `<wishlist-button>`.
   * @returns {boolean} Whether a remove control was found and triggered.
   */
  const removeOwningCartLine = (el) => {
    const row = el.closest('.bv-cart-item, [data-cart-line], [data-item-key]');
    const removeBtn = row && row.querySelector('[data-cart-remove]');
    if (removeBtn instanceof HTMLElement) {
      // Native click bubbles to the delegated handler on <cart-items-component>.
      removeBtn.click();
      return true;
    }
    return false;
  };

  /* --------------------------------------------------------------------- */
  /* Custom element                                                        */
  /* --------------------------------------------------------------------- */

  class WishlistButton extends HTMLElement {
    static get observedAttributes() {
      return ['data-variant-id'];
    }

    connectedCallback() {
      if (!this._bound) {
        this._bound = true;
        this._onClick = this._onClick.bind(this);
        this.addEventListener('click', this._onClick);
      }
      renderElement(this);
    }

    attributeChangedCallback() {
      if (this.isConnected) renderElement(this);
    }

    /** @param {MouseEvent} event */
    async _onClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const productId = this.getAttribute('data-product-id');
      const variantId = this.getAttribute('data-variant-id');
      const sku = this.getAttribute('data-sku');

      if (!isLoggedIn()) {
        storePending({ productId, variantId, sku });
        redirectToLogin();
        return;
      }

      if (!variantId || this.getAttribute('aria-busy') === 'true') return;

      const isCart = this.getAttribute('data-context') === 'cart';

      // Cart / mini-cart "Already in wishlist" is display-only — ignore clicks.
      if (isCart && isInWishlist(variantId)) return;

      this.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      try {
        if (isInWishlist(variantId)) {
          await remove({ productId, variantId });
        } else {
          await add({ productId, variantId, sku });
          // Cart "move to wishlist": once the item is safely saved, drop it from
          // the cart. Done only after the add resolves so the line is never
          // removed if saving fails.
          if (isCart) removeOwningCartLine(this);
        }
      } catch (error) {
        // Surface real failures (network, proxy 4xx/5xx, `success: false`) so
        // they are not silently swallowed. The optimistic state has already
        // been reverted by add()/remove() at this point.
        console.error('[Wishlist] toggle failed', error);
        document.dispatchEvent(
          new CustomEvent('wishlist:error', {
            bubbles: true,
            detail: { productId, variantId, error },
          })
        );
      } finally {
        this.removeAttribute('aria-busy');
        this.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, WishlistButton);

  /**
   * Minimal header element. Re-applies the cached count whenever it (re)connects,
   * so the count survives header re-renders (cart/search/section refresh) without
   * a global observer.
   */
  class WishlistHeader extends HTMLElement {
    connectedCallback() {
      if (state.ready) updateHeader();
    }
  }

  if (!customElements.get('wishlist-header')) {
    customElements.define('wishlist-header', WishlistHeader);
  }

  /* --------------------------------------------------------------------- */
  /* Public API                                                            */
  /* --------------------------------------------------------------------- */

  Object.assign(controller, {
    state,
    isInWishlist,
    sync,
    add,
    remove,
    WishlistAdded,
    WishlistRemoved,
    fetchList,
    removeAll,
    formatPrice,
    updateHeader,
    refresh: async () => {
      if (!isLoggedIn()) return;
      await fetchState();
      updateHeader();
      sync(document);
      syncWishlistButtonsFromProductForm();
    },
  });

  /* --------------------------------------------------------------------- */
  /* Variant change bridge (event-driven, no polling/observer)             */
  /* --------------------------------------------------------------------- */

  const VARIANT_HOST_SELECTOR =
    'product-form-component, quick-view-component, drawer-component, .card-gallery-default, [data-product-id], .shopify-section';

  /**
   * When the theme fires `variant:update`, mirror the new variant id + SKU onto
   * the matching `<wishlist-button>`(s). Changing `data-variant-id` triggers the
   * element's own `attributeChangedCallback`, which re-renders its state.
   * Listens in the capture phase so it runs before product cards stop the event.
   * @param {any} event
   */
  const onVariantUpdate = (event) => {
    const detail = event.detail || {};
    const variant = detail.resource;
    if (!variant || variant.id === null || variant.id === undefined) return;

    const variantId = normalizeId(variant.id);
    const productId =
      detail.data && detail.data.productId !== undefined && detail.data.productId !== null
        ? normalizeId(detail.data.productId)
        : '';

    // When the event carries a product id, it uniquely identifies the product,
    // so update EVERY matching toggle on the page (title icon, inline icon,
    // the heart beside add-to-cart, sticky bar, …) wherever it is nested.
    // Buttons for other products are skipped by the id check below. Without a
    // product id, fall back to a DOM-scoped search: the event usually
    // originates from the variant picker, which can match `[data-product-id]`
    // but not contain the wishlist button (on a product card the button lives
    // in a sibling gallery), so climb until we reach a container that holds one.
    /** @type {ParentNode} */
    let scope = document;
    if (!productId) {
      const target = event.target;
      let found = target instanceof Element ? target.closest(VARIANT_HOST_SELECTOR) : null;
      while (found && !found.querySelector(TAG)) {
        const parent = found.parentElement;
        found = parent ? parent.closest(VARIANT_HOST_SELECTOR) : null;
      }
      scope = found || document;
    }

    /** @param {Element} el */
    const matches = (el) =>
      !productId || normalizeId(el.getAttribute('data-product-id')) === productId;

    // 1) Mirror the new variant id + SKU onto the matching toggles immediately.
    scope.querySelectorAll(TAG).forEach((el) => {
      if (!matches(el)) return;
      if (variant.sku !== undefined && variant.sku !== null && String(variant.sku) !== '') {
        el.setAttribute('data-sku', String(variant.sku));
      }
      el.setAttribute('data-variant-id', variantId);
    });

    // 2) product-form.js morphs `.add-to-cart-button-component` with fresh
    // server HTML later in this SAME event (bubble phase). That morph strips
    // the client-only `is-in-wishlist` class from the heart beside add-to-cart
    // and — because the morphed `data-variant-id` already equals what we just
    // set — does not re-trigger the element's own render. Re-apply state on the
    // next microtask, after that synchronous morph has finished, so every
    // matching toggle (morphed or not) ends up in sync.
    queueMicrotask(() => {
      scope.querySelectorAll(TAG).forEach((el) => {
        if (matches(el)) renderElement(el);
      });
    });
  };

  document.addEventListener('variant:update', onVariantUpdate, true);

  /* --------------------------------------------------------------------- */
  /* Cart / mini-cart re-render bridge                                     */
  /* --------------------------------------------------------------------- */

  /**
   * The cart page and cart drawer re-render their line items through the
   * Section Rendering API on every AJAX change (quantity, remove, clear, free
   * gift toggle, add to cart). That morph swaps in fresh server HTML which has
   * no knowledge of the client-only wishlist state and — because it PRESERVES
   * the existing `<wishlist-button>` nodes rather than replacing them — strips
   * their `is-in-wishlist` class without re-running `connectedCallback`. The
   * result: a saved line shows "Move to wishlist" again after any cart update.
   *
   * The theme dispatches `cart:synced` once, AFTER the morph has settled (see
   * component-cart-items.js and product-form.js), so re-applying state there
   * brings every cart/drawer toggle back in sync. We also listen to
   * `cart:update` as a safety net for paths that morph synchronously and only
   * emit that event; both handlers defer one frame so they run past the morph.
   * sync() is idempotent, so reacting to both events is harmless.
   */
  const resyncCart = () => {
    requestAnimationFrame(() => sync(document));
  };

  document.addEventListener('cart:synced', resyncCart);
  document.addEventListener('cart:update', resyncCart);

  /* --------------------------------------------------------------------- */
  /* Section morph bridge (header hydration, filters, predictive search)   */
  /* --------------------------------------------------------------------- */

  /**
   * `sections/header.liquid` hydrates on load via the Section Rendering API
   * (`section-hydration.js` → `morphSection`). Fresh server HTML resets the
   * wishlist badge to `0` / hidden without re-running `WishlistHeader`
   * `connectedCallback` (the element is morphed in place). Re-apply client
   * state once the morph settles — same deferred-frame pattern as cart sync.
   * @param {CustomEvent<{ section?: Element, sectionId?: string }>} event
   */
  const resyncAfterSectionMorph = (event) => {
    const section = event.detail?.section;
    const scope = section instanceof Element ? section : document;

    requestAnimationFrame(() => {
      if (state.ready) updateHeader();
      sync(scope);
    });
  };

  document.addEventListener(ThemeEvents.sectionMorphed, resyncAfterSectionMorph);

  /* --------------------------------------------------------------------- */
  /* External surface bridge (Live TV app extension, etc.)                 */
  /* --------------------------------------------------------------------- */

  /**
   * Live TV and other app extensions call the same wishlist proxy directly, then
   * dispatch `wishlist:updated` with `detail.action` of `added` or `removed`.
   * Theme cache + header sync here without a duplicate API round-trip. Events
   * from this file include `detail.source === 'theme'` and are ignored.
   * @param {CustomEvent<{ action?: string, productId?: any, variantId?: any, sku?: any, source?: string }>} event
   */
  const onExternalUpdated = (event) => {
    const detail = event.detail || {};
    if (detail.source === 'theme') return;

    const action = detail.action;
    if (action === 'added') {
      syncExternalAdd(
        {
          productId: detail.productId,
          variantId: detail.variantId,
          sku: detail.sku,
        },
        { notify: false }
      );
      return;
    }

    if (action === 'removed') {
      syncExternalRemove(
        {
          productId: detail.productId,
          variantId: detail.variantId,
        },
        { notify: false }
      );
    }
  };

  document.addEventListener(ThemeEvents.wishlistUpdated, onExternalUpdated);

  /* --------------------------------------------------------------------- */
  /* Init                                                                  */
  /* --------------------------------------------------------------------- */

  const init = async () => {
    if (!isLoggedIn()) {
      sync(document);
      return;
    }
    try {
      await fetchState();
      await restorePendingAdd();
    } catch (error) {
      console.error('[Wishlist] initial count/list fetch failed', error);
      // Keep toggles hidden when the API did not load successfully.
      return;
    }
    updateHeader();
    sync(document);
    syncWishlistButtonsFromProductForm();
    dispatchWishlistReady({ source: 'init' });
  };

  /**
   * Dispatch `wishlist:ready` for carousel/page listeners after cache is warm.
   * @param {{ source?: string }} [detail]
   */
  const dispatchWishlistReady = (detail = {}) => {
    document.dispatchEvent(
      new CustomEvent('wishlist:ready', {
        bubbles: true,
        detail: { count: state.count, itemCount: state.items.length, ...detail },
      })
    );
  };

  /**
   * Count can stay warm while `state.items` was cleared by a paginated page fetch
   * or navigation. Reload the init cache when that mismatch is detected.
   */
  const ensureCacheWarm = async () => {
    if (!isLoggedIn() || !state.ready) return false;
    if (state.count <= 0) return false;
    if (state.items.length > 0 && state.variantSet.size > 0) return false;
    try {
      await fetchState();
      return true;
    } catch (error) {
      console.error('[Wishlist] cache refresh failed', error);
      return false;
    }
  };

  /**
   * Align PDP `<wishlist-button>` toggles with the active variant from
   * `product-form-component` (before `variant:update`, e.g. carousel `?variant=` links).
   */
  const syncWishlistButtonsFromProductForm = () => {
    document.querySelectorAll('product-form-component').forEach((form) => {
      if (!(form instanceof HTMLElement)) return;
      const productId = normalizeId(form.dataset.productId);
      const variantInput = form.querySelector('input[name="id"]');
      if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return;

      const variantId = normalizeId(variantInput.value);
      if (!variantId) return;

      form.querySelectorAll(TAG).forEach((el) => {
        if (productId && normalizeId(el.getAttribute('data-product-id')) !== productId) return;
        el.setAttribute('data-variant-id', variantId);
        renderElement(el);
      });
    });
  };

  const rehydrateAfterNavigation = async () => {
    if (!isLoggedIn()) {
      sync(document);
      return;
    }

    if (!state.ready) return;

    const refreshed = await ensureCacheWarm();
    updateHeader();
    sync(document);
    syncWishlistButtonsFromProductForm();

    if (refreshed) dispatchWishlistReady({ source: 'navigation' });
  };

  // Re-sync after back/forward (bfcache) and same-tab navigations (home ↔ PDP).
  window.addEventListener('pageshow', () => {
    requestAnimationFrame(() => {
      rehydrateAfterNavigation();
    });
  });

  // View Transitions API (MPA product navigation) — pagereveal runs on the new page.
  window.addEventListener('pagereveal', () => {
    requestAnimationFrame(() => {
      rehydrateAfterNavigation();
    });
  });

  // This file is loaded as a deferred module, so the DOM is already parsed by
  // the time it runs. Initialize immediately so count + list fire ASAP.
    init();
})();
